/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  ChannelsService,
  PreviewService,
  TGridGuideServiceFetchGuideRowsParams,
  DateTime,
  GridGuideService,
  TChannel,
  TChannelsType,
  TChannels,
  TGridGuideRow,
  type TPreviewServiceGetOfferPreviewParamsV4,
  type TPreviewServiceGetOfferPreviewParamsV5,
  type TPreview,
  TsriServiceEndpointService,
  ChannelSettingService,
  SourceType,
  NpvrRecordingCapabilities
} from "@tivocorp/data-domain-lib";

import type { IChannelsInteractor } from "../interfaces/IChannelsInteractor";
import { Log } from "../logging/Log";
import { ApplicationFeatureArea } from "../enums";
import { TIVO_SERVICES } from "../../config";
import { BaseInteractor } from "./base";
import { PlaybackContentType } from "../videoplayer/enums";
import { IPlayable } from "../videoplayer/interfaces";
import { VideoProviderConfigModel } from "../videoProvider";
import { DeviceModel } from "../device";
import type { TNetworkOptions } from "../types/TNetworkOptions";
import { DimensionScaler } from "../utility";

/**
 * Facade class for general service operations (v1).
 *
 * Service instances are **not** stored as class members. A fresh
 * instance is created on every method call so the interactor holds
 * no mutable state. TServicesParams is built internally; callers
 * do not supply it.
 */
export class ChannelsInteractor
  extends BaseInteractor
  implements IChannelsInteractor
{
  private static readonly TAG: string = "ChannelsInteractor";

  public async fetchAllChannels(
    offset?: number,
    limit?: number
  ): Promise<TChannels> {
    try {
      Log.d(ChannelsInteractor.TAG, "fetchAllChannels() called");
      const service = await this.createChannelsService();
      return await service.fetchAllChannels(offset, limit);
    } catch (error: unknown) {
      Log.e(ChannelsInteractor.TAG, "fetchAllChannels()", error);
      throw error;
    }
  }

  public async fetchMyChannels(
    offset?: number,
    limit?: number
  ): Promise<TChannels> {
    try {
      Log.d(ChannelsInteractor.TAG, "fetchMyChannels() called");
      const service = await this.createChannelsService();
      return await service.fetchMyChannels(offset, limit);
    } catch (error: unknown) {
      Log.e(ChannelsInteractor.TAG, "fetchMyChannels()", error);
      throw error;
    }
  }

  public async fetchFavoriteChannels(
    offset?: number,
    limit?: number
  ): Promise<TChannels> {
    try {
      Log.d(ChannelsInteractor.TAG, "fetchFavoriteChannels() called");
      const service = await this.createChannelsService();
      return await service.fetchFavoriteChannels(offset, limit);
    } catch (error: unknown) {
      Log.e(ChannelsInteractor.TAG, "fetchFavoriteChannels()", error);
      throw error;
    }
  }

  public getLastPlayedOrFallbackToFirstByChannelNumberV2 = async (
    channelNumber: string | null
  ): Promise<IPlayable> => {
    let channel: TChannel;
    try {
      if (channelNumber === null)
        throw new Error("Last tuned channel not found.");
      channel = await this.tuneToChannel(channelNumber);
    } catch (error) {
      Log.e(
        this.constructor.name,
        "getLastPlayedOrFallbackToFirstByChannelNumberV2()->Failed to get channel from channel number:",
        error
      );
      const channels: TChannels = await this.fetchAllChannels(0, 1);
      channel = channels.channels[0];
    }
    return { channel, playbackContentType: PlaybackContentType.LIVE };
  };

  public async fetchNextChannelsFromChannelNumber(
    channelNumber: string,
    channelsType: TChannelsType,
    limit: number
  ): Promise<TChannels> {
    try {
      Log.d(
        ChannelsInteractor.TAG,
        "fetchNextChannelsFromChannelNumber() called"
      );
      const service = await this.createChannelsService();
      return await service.fetchNextChannelsFromChannelNumber(
        channelNumber,
        channelsType,
        limit
      );
    } catch (error: unknown) {
      Log.e(
        ChannelsInteractor.TAG,
        "fetchNextChannelsFromChannelNumber()",
        error
      );
      throw error;
    }
  }

  public async getNextChannel(
    channelNumber: string,
    channelsType: TChannelsType,
    count: number = 1
  ): Promise<TChannel> {
    try {
      Log.d(
        ChannelsInteractor.TAG,
        `getNextChannel() — current: ${channelNumber}, type: ${channelsType}`
      );
      const service = await this.createChannelsService();
      return await service.getNextChannel(channelNumber, channelsType, count);
    } catch (error: unknown) {
      Log.e(ChannelsInteractor.TAG, "getNextChannel()", error);
      throw error;
    }
  }

  public async getPreviousChannel(
    channelNumber: string,
    channelsType: TChannelsType,
    count: number = 1
  ): Promise<TChannel> {
    try {
      Log.d(
        ChannelsInteractor.TAG,
        `getPreviousChannel() — current: ${channelNumber}, type: ${channelsType}`
      );
      const service = await this.createChannelsService();
      return await service.getPreviousChannel(
        channelNumber,
        channelsType,
        count
      );
    } catch (error: unknown) {
      Log.e(ChannelsInteractor.TAG, "getPreviousChannel()", error);
      throw error;
    }
  }

  public async tuneToChannel(channelNumber: string): Promise<TChannel> {
    try {
      Log.d(ChannelsInteractor.TAG, "tuneToChannel() called");
      const service = await this.createChannelsService();
      return await service.tuneToChannel(channelNumber);
    } catch (error: unknown) {
      Log.e(ChannelsInteractor.TAG, "tuneToChannel()", error);
      throw error;
    }
  }

  public async updateFavoriteChannel(
    channel: TChannel,
    isFavorite: boolean
  ): Promise<void> {
    try {
      Log.d(ChannelsInteractor.TAG, "updateFavoriteChannel() called");
      const service = await this.createChannelsService();
      return await service.updateFavoriteChannel(channel, isFavorite);
    } catch (error: unknown) {
      Log.e(ChannelsInteractor.TAG, "updateFavoriteChannel()", error);
      throw error;
    }
  }

  public async updateChannelUrl(
    channelNumber: string,
    url: string
  ): Promise<void> {
    try {
      Log.d(ChannelsInteractor.TAG, "updateChannelUrl() called");
      const service = await this.createChannelsService();
      return await service.updateChannelUrl(channelNumber, url);
    } catch (error: unknown) {
      Log.e(ChannelsInteractor.TAG, "updateChannelUrl()", error);
      throw error;
    }
  }

  public async fetchFilteredOutChannels(): Promise<Array<TChannel>> {
    try {
      Log.d(ChannelsInteractor.TAG, "fetchFilteredOutChannels() called");
      const service = await this.createChannelsService();
      return await service.fetchFilteredOutChannels();
    } catch (error: unknown) {
      Log.e(ChannelsInteractor.TAG, "fetchFilteredOutChannels()", error);
      throw error;
    }
  }

  private async createChannelsService(): Promise<ChannelsService> {
    const channelLogoBaseUrl = await this.getServiceEndpointByServiceName(
      TIVO_SERVICES.ChannelLogoImageHost
    );
    return new ChannelsService(
      await this.buildServicesParams(
        TIVO_SERVICES.ChannelsIptv,
        ApplicationFeatureArea.HOME
      ),
      channelLogoBaseUrl
    );
  }

  /**
   * Looks up a single subscribed channel by stationId from the
   * Storage cache (Storage.Keys.CHANNELS).
   * Returns null when not found or cache is empty.
   *
   * @param {string} stationId - The stationId to look up
   * @returns {Promise<TChannel | null>} Matching subscribed channel or null
   */
  public async getChannelByStationId(
    stationId: string
  ): Promise<TChannel | null> {
    const service = await this.createChannelsService();
    return service.getChannelByStationId(stationId);
  }

  /**
   * Resolves guide rows to the stations the backend actually returned them for.
   *
   * Data unavailability is never encoded as guide data: gaps in a row and
   * stations that returned no programs are left as-is. The renderer fills any
   * uncovered span with a loading placeholder (`buildRowCells`), and the cache
   * records an empty answer separately from real programs, so a missing span
   * can never hide or displace a real program that arrives later.
   *
   * @param guideRows - Raw guide rows from the service
   * @param stationIds - Station IDs in display order
   * @returns Guide rows for the stations that returned one, in request order
   */
  private validateGuideRows(
    guideRows: TGridGuideRow[],
    stationIds: string[]
  ): TGridGuideRow[] {
    // Index the response by the station the backend actually returned it for.
    // The service layer omits stations it has no data for, so the response can
    // be shorter than the requested list — matching by array position would
    // attribute a row to the wrong station.
    const rowByStationId: Map<string, TGridGuideRow> = new Map();
    guideRows.forEach((guideRow): void => {
      if (guideRow && guideRow.stationId) {
        rowByStationId.set(guideRow.stationId, guideRow);
      }
    });

    const validatedRows: TGridGuideRow[] = [];
    const missingStationIds: string[] = [];

    stationIds.forEach((stationId): void => {
      const guideRow: TGridGuideRow | undefined = rowByStationId.get(stationId);

      if (guideRow === undefined) {
        // No row came back for this station. Omit it rather than synthesising
        // one, so the cache does not record the window as fetched and the
        // station stays refetchable.
        missingStationIds.push(stationId);
        return;
      }

      // The station answered — pass its real programs through untouched. An
      // empty array is a valid answer ("responded with no programs") and is
      // recorded by the cache as an empty range, never as a program.
      validatedRows.push({
        stationId,
        guideCells: guideRow.guideCells
      });
    });

    if (missingStationIds.length > 0) {
      Log.w(
        ChannelsInteractor.TAG,
        "validateGuideRows() - no guide row returned for stations: " +
          missingStationIds.join(", ")
      );
    }

    return validatedRows;
  }

  /**
   * @inheritdoc
   */
  public async fetchGuideRows(
    params: Omit<
      TGridGuideServiceFetchGuideRowsParams,
      "msoPartnerId" | "originRequestId"
    >,
    options?: TNetworkOptions
  ): Promise<TGridGuideRow[]> {
    return this.executeGuarded(
      "fetchGuideRows",
      async (guardedSignal: AbortSignal): Promise<TGridGuideRow[]> => {
        Log.d(ChannelsInteractor.TAG, "fetchGuideRows() called");
        const service: GridGuideService = new GridGuideService(
          await this.buildServicesParams(
            TIVO_SERVICES.Guide,
            ApplicationFeatureArea.GUIDE
          ),
          await this.getServiceEndpointByServiceName(
            TIVO_SERVICES.ChannelLogoImageHost
          )
        );
        return service.fetchGuideRows(
          {
            ...params,
            msoPartnerId: await DeviceModel.getMsoPartnerId(),
            originRequestId: "" // TODO - generate unique origin request ID for logging/tracing
          },
          guardedSignal
        );
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async fetchGuideRowsWithFallback(
    params: {
      channels: TChannel[];
      startTime: DateTime;
      endTime: DateTime;
    },
    options?: TNetworkOptions
  ): Promise<TGridGuideRow[]> {
    const { channels, startTime, endTime } = params;
    const stationIds: string[] = channels.map(
      ({ stationId = "" }) => stationId
    );

    try {
      const guideRows: TGridGuideRow[] = await this.fetchGuideRows(
        {
          stationId: stationIds,
          windowStartTime: startTime,
          windowEndTime: endTime
        },
        options
      );

      return this.validateGuideRows(guideRows, stationIds);
    } catch (error: unknown) {
      Log.e(
        ChannelsInteractor.TAG,
        "fetchGuideRowsWithFallback() - guide rows service failed",
        error
      );

      throw error;
    }
  }

  /**
   * @inheritdoc
   */
  public async fetchOfferPreview(
    params: TPreviewServiceGetOfferPreviewParamsV4,
    options?: TNetworkOptions
  ): Promise<TPreview> {
    return this.executeGuarded(
      "fetchOfferPreview",
      async (guardedSignal: AbortSignal): Promise<TPreview> => {
        Log.d(ChannelsInteractor.TAG, "fetchOfferPreview() called");
        const previewService = new PreviewService(
          await this.buildServicesParams(
            TIVO_SERVICES.PreviewService,
            ApplicationFeatureArea.GUIDE
          )
        );
        const res: TPreview = await previewService.fetchOfferPreviewV4(
          params,
          true,
          guardedSignal
        );
        return res;
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async fetchOfferPreviewV5(
    params: TPreviewServiceGetOfferPreviewParamsV5,
    options?: TNetworkOptions
  ): Promise<TPreview> {
    return this.executeGuarded(
      "fetchOfferPreviewV5",
      async (guardedSignal: AbortSignal): Promise<TPreview> => {
        Log.d(ChannelsInteractor.TAG, "fetchOfferPreviewV5() called");
        const previewService = new PreviewService(
          await this.buildServicesParams(
            TIVO_SERVICES.PreviewService,
            ApplicationFeatureArea.GUIDE
          )
        );
        const res: TPreview = await previewService.fetchOfferPreviewV5(
          params,
          true,
          guardedSignal
        );
        return res;
      },
      options
    );
  }

  public static getChannelUrl(
    params: { height: number; width: number } & (
      | { isJumpChannel: false; logoIndex: number }
      | { isJumpChannel: true; objectId: string }
    )
  ): string {
    const scaler = DimensionScaler.getInstance();
    const scaledHeight = scaler.getScaledHeight(params.height);
    const scaledWidth = scaler.getScaledWidth(params.width);
    const channelImageBaseUrl =
      TsriServiceEndpointService.getServiceEndpointByServiceName(
        TIVO_SERVICES.ChannelLogoImageHost
      );

    const channelUrl = params.isJumpChannel
      ? `${channelImageBaseUrl}/images?objectid=${params.objectId}&imagetype=partnerSourceLogo&width=${scaledWidth}&height=${scaledHeight}`
      : `${channelImageBaseUrl}/logos/${scaledWidth}x${scaledHeight}/${params.logoIndex - 65536}.png`;

    return channelUrl;
  }

  /**
   * @inheritdoc
   */
  public async setChannelSettings(
    channelNumber: string,
    isFavorite: boolean,
    options?: TNetworkOptions
  ): Promise<boolean> {
    return this.executeGuarded(
      "setChannelSettings",
      async (guardedSignal: AbortSignal): Promise<boolean> => {
        Log.d(ChannelsInteractor.TAG, "setChannelSettings() called");
        const service = new ChannelSettingService(
          await this.buildServicesParams(
            TIVO_SERVICES.ChannelSettingService,
            ApplicationFeatureArea.GUIDE
          )
        );
        return service.setChannelSettings(
          { contentType: "", channelSettings: [{ channelNumber, isFavorite }] },
          guardedSignal
        );
      },
      options
    );
  }

  public static isChannelRecordable(channel: TChannel): boolean {
    if (!channel) return false;
    if (!channel.isSubscribed) return false; // Entitlement check
    if (channel.sourceType !== SourceType.IpStream) return false;
    // TiVo+ channels are never recordable, whatever their policy says. The
    // rule is a property of the service rather than of the station, so it is
    // asserted here rather than trusted to arrive in policyInfo.
    if (ChannelsInteractor.isTivoPlusChannel(channel)) return false;

    const { policyInfo = [] } = channel;
    const policyRecordable: boolean =
      policyInfo.some((data) => {
        return !!(
          data.recordingRules &&
          data.recordingRules.restrictedCapability &&
          data.recordingRules.restrictedCapability.includes(
            NpvrRecordingCapabilities.AllowRecording
          )
        );
      }) || false;

    return policyRecordable;
  }

  /**
   * Whether this app can tune the given channel itself.
   *
   * `AppLinear` channels are carried in the lineup and rendered in the guide
   * (see {@link getAppBasedIconUrl}), but they play inside a separate partner
   * app rather than in this player. Deep-linking out to another app is not
   * supported, so there is nothing here to tune — the channel is skipped
   * rather than handed to the player, which would otherwise attempt a stream
   * it can never resolve.
   *
   * `AppLinear` is the only untunable source type; every other channel,
   * including `Jump`, stays playable.
   */
  public static isTunableChannel(channel: TChannel): boolean {
    if (!channel) return false;

    return channel.sourceType !== SourceType.AppLinear;
  }

  /**
   * Determines whether the given channel is a TiVo Plus channel.
   *
   * A channel qualifies as TiVo Plus when:
   * 1. sourceType is IpStream
   * 2. linearProviderPartnerId is non-empty
   * 3. The associated VideoProviderConfiguration has a sessionManagerConfig
   *    declaring a TiVo+ sessionManagerType
   *
   * Step 3 is a relay of server-declared provider configuration, never a
   * client-side decision. It reads the configuration DrmManager already
   * caches, so this stays synchronous and makes no network call — important
   * because it runs on the tuning path and inside Redux selectors.
   *
   * An unknown provider yields `false`. Callers that must distinguish
   * "not TiVo+" from "configuration not loaded yet" should check
   * {@link VideoProviderConfigModel.isConfigurationLoaded} themselves.
   */
  public static isTivoPlusChannel(channel: TChannel): boolean {
    if (!channel) return false;
    if (channel.sourceType !== SourceType.IpStream) return false;
    if (!channel.linearProviderPartnerId) return false;

    return VideoProviderConfigModel.isTivoPlusProviderPartnerId(
      channel.linearProviderPartnerId
    );
  }

  /**
   * Returns the partner-source-logo URL for an app-linear channel, or null
   * if the channel does not qualify (sourceType !== AppLinear or missing partnerId).
   */
  public static getAppBasedIconUrl(
    channel: TChannel,
    height: number,
    width: number
  ): string | null {
    if (!channel) return null;
    if (channel.sourceType !== SourceType.AppLinear) return null;
    if (!channel.linearProviderPartnerId) return null;

    return ChannelsInteractor.getChannelUrl({
      isJumpChannel: true,
      objectId: channel.linearProviderPartnerId,
      height,
      width
    });
  }
}
